import posthog from 'posthog-js'
import Vue from 'vue'

export default function ({ $config, app, store }, inject) {
  const posthogToken =
    typeof $config.posthogToken === 'string' ? $config.posthogToken.trim() : ''
  const isDevMode = process.env.NODE_ENV !== 'production'
  let isPosthogReady = false
  let pendingIdentifyUser = null

  const warnIdentifySkipped = reason => {
    if (!isDevMode || typeof console === 'undefined' || typeof console.warn !== 'function') {
      return
    }

    console.warn(`[posthog] identify skipped: ${reason}`)
  }
  const normalizeCreatedAt = value => {
    if (!value) return null

    const parsedDate = new Date(value)
    return Number.isNaN(parsedDate.getTime()) ? null : parsedDate.toISOString()
  }
  const normalizeNonEmptyString = value => {
    if (typeof value !== 'string') return null
    const trimmed = value.trim()
    return trimmed.length > 0 ? trimmed : null
  }
  const getOrganizationId = user => user?.current_organization?.id ?? null
  const identifyUserAndCompanyGroup = user => {
    if (!user?.id) {
      warnIdentifySkipped('missing user id')
      return
    }

    if (!posthogToken) {
      warnIdentifySkipped('posthog token missing')
      return
    }

    if (!isPosthogReady || typeof posthog?.identify !== 'function') {
      pendingIdentifyUser = user
      warnIdentifySkipped('PostHog SDK not ready yet; queued identify for loaded callback')
      return
    }

    const analyticsUserType = user.type === 'applicant' ? 'candidate' : 'employer'
    const resolvedName =
      normalizeNonEmptyString(user.name) ||
      normalizeNonEmptyString(user.fullName) ||
      normalizeNonEmptyString(`${user.firstname || ''} ${user.lastname || ''}`)
    const identifyPayload = {
      email: user.email,
      user_type: analyticsUserType,
      created_at: normalizeCreatedAt(user.created_at),
    }
    if (resolvedName) {
      identifyPayload.name = resolvedName
    }

    posthog.identify(user.id.toString(), identifyPayload)

    const organizationId = getOrganizationId(user)
    if (organizationId && typeof posthog?.group === 'function') {
      posthog.group('company', organizationId.toString(), {
        organization_id: organizationId.toString(),
      })
    }
  }

  inject('posthog', posthog)
  inject('identifyPosthogUser', identifyUserAndCompanyGroup)

  if (!posthogToken) {
    return
  }

  // Initialize PostHog
  posthog.init(posthogToken, {
	  api_host: 'https://eu.i.posthog.com',
	  defaults: '2025-05-24',
	  person_profiles: 'identified_only',

    // CRITICAL: Use localStorage instead of cookies to prevent HTTP header size issues
    persistence: 'localStorage+cookie', // Use localStorage as primary, cookies as fallback

    // Reduce cookie size by limiting what's stored in cookies
    disable_cookie: false, // Keep cookies for cross-subdomain tracking
    cookie_expiration: 30, // Reduce from default 365 days to 30 days

    // Additional configuration for better integration
    capture_pageview: false, // We'll handle this manually for SPA routing
    capture_pageleave: true,
	disable_session_recording: false , // Enable session recording for all environments
   // disable_session_recording: $config.deployEnv === 'production' ? false : true, // Only record in production

    // Privacy settings
    respect_dnt: true,
    opt_out_capturing_by_default: false,

    // Performance settings
    batch_send_requests: true,

    // Debug mode for development
    loaded: function(posthog) {
      isPosthogReady = true

      // Register super properties for ALL events - APR-981
      posthog.register({
        environment: $config.deployEnv || 'development', // 'production' | 'staging' | 'development'
        platform: 'web',
      })

      if (pendingIdentifyUser?.id) {
        identifyUserAndCompanyGroup(pendingIdentifyUser)
        pendingIdentifyUser = null
      }

      if ($config.debug) {
        posthog.debug()
      }

	  const gtmContainerId = $config.gtmContainerId
	  const canLoadGtm =
		  typeof gtmContainerId === 'string' &&
		  gtmContainerId.length > 0

	  if (canLoadGtm) {
		  const gtmSrcPrefix =
			  'https://www.googletagmanager.com/gtm.js?id=' + gtmContainerId
		  const hasGtmScript = Array.from(
			  document.getElementsByTagName('script')
		  ).some(
			  script => typeof script?.src === 'string' && script.src.startsWith(gtmSrcPrefix)
		  )

		  if (!hasGtmScript) {
			  ;(function(w, d, s, l, i) {
				  w[l] = w[l] || []
				  w[l].push({ 'gtm.start': new Date().getTime(), event: 'gtm.js' })
				  var f = d.getElementsByTagName(s)[0],
					  j = d.createElement(s),
					  dl = l != 'dataLayer' ? '&l=' + l : ''
				  j.async = true
				  j.src = 'https://www.googletagmanager.com/gtm.js?id=' + i + dl
				  f.parentNode.insertBefore(j, f)
			  })(window, document, 'script', 'dataLayer', gtmContainerId)
		  }
	  }
    }
  })

  const withoutApplySource = route => {
    const query = { ...route.query }
    delete query.apply_source
    return app.router.resolve({ path: route.path, query, hash: route.hash }).route
  }

  // Attribution cleanup is part of the same visit, not another page view.
  app.router.afterEach((to, from) => {
    const pageviewRoute = withoutApplySource(to)
    if (
      from?.query?.apply_source !== undefined &&
      to.query?.apply_source === undefined &&
      withoutApplySource(from).fullPath === pageviewRoute.fullPath
    ) {
      return
    }

    // Wait for the next tick to ensure the page has loaded
    Vue.nextTick(() => {
      posthog.capture('$pageview', {
        $current_url: pageviewRoute.fullPath,
        $host: window.location.host,
        $pathname: pageviewRoute.path,
        $search: pageviewRoute.query
      })
    })
  })

  if (store.state.user && store.state.user.user && store.state.user.user.id) {
    identifyUserAndCompanyGroup(store.state.user.user)
  }

  // Watch for user login/logout events
  store.watch(
    (state) => state.user.user,
    (newUser, oldUser) => {
      const userChanged = !!(newUser && newUser.id && (!oldUser || oldUser.id !== newUser.id))
      const organizationChanged =
        getOrganizationId(newUser) !== getOrganizationId(oldUser)

      if (newUser && newUser.id && (userChanged || organizationChanged)) {
        // User logged in/switched or changed organization context
        identifyUserAndCompanyGroup(newUser)
      } else if (!newUser && oldUser) {
        // User logged out
        posthog.reset()
      }
    }
  )
}
